<template>
  <form @submit.prevent="submit">
    <StyledDateInput
      v-model="birthday"
      :class="$style.dateInput"
      @input="error = null"
    />

    <UpdateFormButton
      :class="$style.button"
      :error="error"
      :is-loading="isLoading"
    >
      <template slot="error">
        {{ error && error.message }}
      </template>
      <template slot="loading">
        {{ $t('button.loading') }}
      </template>
      <template>
        {{ $t('button.submit') }}
      </template>
    </UpdateFormButton>
  </form>
</template>

<script lang="ts">

import Vue from 'vue';
import { meApi } from '@/scripts/webcall/v3/me';
import loginController from '@/scripts/libs/login/controller';
import StyledDateInput from './StyledDateInput.ts.vue';
import UpdateFormButton from './UpdateFormButton.ts.vue';

class UpdateFormError extends Error {};

export default Vue.extend({
  i18n: {
    en: {
      error: {
        'invalid-input': 'Input is invalid',
        'error-try-again': 'Error. Try again',
      },
      button: {
        loading: 'updating',
        submit: 'Confirm',
      },
    },
    id: {
      error: {
        'invalid-input': 'Masukan tidak valid',
        'error-try-again': 'Kesalahan. Coba lagi',
      },
      button: {
        loading: 'mohon tunggu',
        submit: 'Konfirmasi',
      },
    },
    zh: {
      error: {
        'invalid-input': '輸入有錯誤',
        'error-try-again': '出現錯誤，請重試',
      },
      button: {
        loading: '請稍候',
        submit: '確認',
      },
    },
  },

  components: {
    StyledDateInput,
    UpdateFormButton,
  },

  data() {
    return {
      birthday: null as string | null,
      error: null as Error | null,
      isLoading: false,
    };
  },

  methods: {
    async submit(): Promise<void> {
      this.$emit('submit');

      this.error = null;
      this.isLoading = true;

      try {
        const birthdayToSubmit = this.birthday;
        if (!this.validateBirthday(birthdayToSubmit)) {
          throw new UpdateFormError(this.$t('error.invalid-input'));
        };

        const user = await loginController.getUser();
        if (user == null) throw new Error('User cannot be null');

        await meApi.profile.update(undefined, undefined, {
          birthday: birthdayToSubmit,
          firstName: user.profile.firstName,
          lastName: user.profile.lastName,
        })
          .catch((error) => {
            if ('request' in error) {
              // is Axios error
              switch (error.request.status) {
                case 400:
                  throw new UpdateFormError(this.$t('error.invalid-input'));
                default:
                  throw new UpdateFormError(this.$t('error.error-try-again'));
              }
            }
            throw error;
          })
        ;

        this.$emit('done');
      } catch (error) {
        if (error instanceof UpdateFormError) {
          this.error = new Error(error.message);
        } else {
          throw error;
        }
      } finally {
        this.isLoading = false;
      }
    },

    validateBirthday(birthday: string | null): birthday is string {
      if (birthday == null) {
        return false;
      }

      // Check if birthdate is valid
      const d = new Date(birthday);
      const rebuilt = [
        `${d.getFullYear()}`.padStart(4, '0'),
        `${d.getMonth() + 1}`.padStart(2, '0'),
        `${d.getDate()}`.padStart(2, '0'),
      ].join('-');

      if (rebuilt !== birthday) {
        return false;
      }

      return true;
    },
  },
});
</script>

<style lang="scss" module>
$focus-bg-color: #fff2f7;

.dateInput :global {
  input {
    &:focus {
      background-color: $focus-bg-color;
    }
  }
}
.button {
  margin-top: 4vw;
  @media screen and (min-width: 360px) {
    margin-top: 1.5rem;
  }
}
</style>
